import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { todayISO } from "@/lib/utils-app";
import { Plus, Trash2, Calculator, CalendarClock, Repeat, ListChecks } from "lucide-react";

// V1.32 · Nuevo diálogo de contrato con desglose económico profesional,
// líneas dinámicas desde el pack, descuento global, entrada, saldo pendiente
// y fraccionamiento en modo simple o recurrente.

const EMPTY = {
  pack_id: "",
  pack_name: "",
  fecha: todayISO(),
  permiso: "B",
  line_items: [],
  precio_lista: 0,
  descuento_pct: 0,
  descuento_importe: 0,
  precio_venta: 0,
  precio_final: 0,
  matricula: 0,
  tasa_dgt: 0,
  precio_clase: 0,
  precio_pack5: 0,
  total: 0,
  entrada: 0,
  saldo: 0,
  segundo_pago: 0,
  fecha_entrada: todayISO(),
  cuotas: [],
  cuotas_recurrentes: null,
  consents: {
    proteccion_datos: false,
    uso_imagen: false,
    autorizacion_dgt: true,
    promociones: false,
    autoriza_gestion_documental: true,
    importe_gestion_documental: 0,
  },
  notes: "",
};

// Deriva líneas económicas reales desde un pack seleccionado
const deriveLineItemsFromPack = (pack) => {
  if (!pack) return [];
  const items = [];
  const push = (descripcion, cantidad, precio_unitario) => {
    if (precio_unitario > 0 && cantidad > 0) {
      items.push({
        descripcion,
        cantidad,
        precio_unitario: +Number(precio_unitario).toFixed(2),
        importe: +(cantidad * precio_unitario).toFixed(2),
      });
    }
  };
  push("Matrícula", 1, pack.matricula);
  push("Tasa DGT 2.1", 1, pack.tasa_dgt);
  const nc = Number(pack.clases_practicas || 0);
  if (nc > 0) push(`Clases prácticas (${nc} × 45 min)`, nc, pack.precio_clase);
  const nip = Number(pack.intentos_practico || 0);
  if (nip > 0 && Number(pack.precio_pack5 || 0) > 0) {
    push(`Pack 5 clases / re-presentación (× ${nip})`, nip, pack.precio_pack5);
  }
  return items;
};

// V1.42.10 · Precio unitario "todo incluido" del pack — se vuelca en la línea
// cuando el usuario selecciona el pack como descripción. Usa `p.total` si el
// backend lo devuelve, si no lo calcula sumando los componentes.
const packUnitPrice = (pack) => {
  if (!pack) return 0;
  if (Number(pack.total) > 0) return +Number(pack.total).toFixed(2);
  const nc = Number(pack.clases_practicas || 0);
  const nip = Number(pack.intentos_practico || 0);
  const total = Number(pack.matricula || 0)
    + Number(pack.tasa_dgt || 0)
    + nc * Number(pack.precio_clase || 0)
    + nip * Number(pack.precio_pack5 || 0);
  return +total.toFixed(2);
};

// Añade días a una fecha ISO y devuelve nueva fecha ISO
const addMonthsISO = (iso, months) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

// Formatea 1234.56 → "1.234,56 €"
const fmtEur = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
};

export function ContractForm({ studentId, contract, onSaved, onCancel, active = true }) {
  const [packs, setPacks] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [cuotasMode, setCuotasMode] = useState("simple"); // simple | recurrente

  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        const { data } = await api.get("/packs");
        setPacks(data || []);
      } catch (err) {
        console.warn("packs:", err);
      }
    })();
    if (contract) {
      setForm({ ...EMPTY, ...contract, consents: { ...EMPTY.consents, ...(contract.consents || {}) } });
      setCuotasMode(contract.cuotas_recurrentes ? "recurrente" : "simple");
    } else {
      setForm({ ...EMPTY });
      setCuotasMode("simple");
    }
  }, [active, contract]);

  // Recálculo económico automático cada vez que cambian las líneas o el descuento
  const econ = useMemo(() => {
    const items = form.line_items || [];
    // Precio de lista real = Σ (cantidad × precio unitario)
    const precio_lista = +items.reduce(
      (a, i) => a + Number(i.cantidad || 0) * Number(i.precio_unitario || 0), 0
    ).toFixed(2);
    // Suma de importes (editables por línea como "precio de venta")
    const venta_lines = +items.reduce((a, i) => a + Number(i.importe || 0), 0).toFixed(2);
    const ventaGlobal = Number(form.precio_venta || 0);
    // Prioridad: precio de venta global > importes de línea modificados > descuento %
    const venta = ventaGlobal > 0
      ? ventaGlobal
      : (Math.abs(venta_lines - precio_lista) > 0.009 ? venta_lines : 0);
    let pct, descuento_importe, precio_final;
    if (venta > 0 && precio_lista > 0) {
      // V1.55 · Precio de venta pactado → descuento derivado automáticamente
      descuento_importe = +Math.max(0, precio_lista - venta).toFixed(2);
      pct = +(descuento_importe / precio_lista * 100).toFixed(2);
      precio_final = +venta.toFixed(2);
    } else {
      pct = Math.min(100, Math.max(0, Number(form.descuento_pct || 0)));
      descuento_importe = +(precio_lista * pct / 100).toFixed(2);
      precio_final = +(precio_lista - descuento_importe).toFixed(2);
    }
    const entrada = Number(form.entrada || 0);
    const saldo = +Math.max(0, precio_final - entrada).toFixed(2);
    // IVA 21% incluido en el precio de venta → desglose informativo
    const base_imponible = +(precio_final / 1.21).toFixed(2);
    const iva_importe = +(precio_final - base_imponible).toFixed(2);
    return { precio_lista, descuento_pct: pct, descuento_importe, precio_venta: venta, precio_final, saldo, base_imponible, iva_importe };
  }, [form.line_items, form.descuento_pct, form.precio_venta, form.entrada]);

  // Cuotas plan preview (modo recurrente)
  const cuotasPreview = useMemo(() => {
    if (cuotasMode !== "recurrente" || !form.cuotas_recurrentes) return null;
    const { num_cuotas, importe_cuota, fecha_inicio } = form.cuotas_recurrentes;
    if (!num_cuotas || !fecha_inicio) return null;
    const suma = +(num_cuotas * importe_cuota).toFixed(2);
    const ultima = addMonthsISO(fecha_inicio, num_cuotas - 1);
    return { suma, ultima };
  }, [cuotasMode, form.cuotas_recurrentes]);

  const applyPack = (pack_id) => {
    if (!pack_id) {
      setForm((f) => ({ ...f, pack_id: "", pack_name: "" }));
      return;
    }
    const p = packs.find((x) => x.id === pack_id);
    if (!p) { setForm((f) => ({ ...f, pack_id })); return; }
    const items = deriveLineItemsFromPack(p);
    setForm((f) => ({
      ...f,
      pack_id,
      pack_name: p.name,
      line_items: items,
      matricula: p.matricula || 0,
      tasa_dgt: p.tasa_dgt || 0,
      precio_clase: p.precio_clase || 0,
      precio_pack5: p.precio_pack5 || 0,
    }));
  };

  const updateItem = (idx, patch) => {
    setForm((f) => {
      const next = [...(f.line_items || [])];
      const merged = { ...next[idx], ...patch };
      merged.importe = +(Number(merged.cantidad || 0) * Number(merged.precio_unitario || 0)).toFixed(2);
      merged.importe_manual = false;
      next[idx] = merged;
      return { ...f, line_items: next };
    });
  };
  // V1.55 · Importe editable como "precio de venta" de la línea — al escribir
  // (ej: 1099 sobre 1349) el descuento y el IVA se calculan automáticamente.
  const setItemImporte = (idx, value) => setForm((f) => {
    const next = [...(f.line_items || [])];
    next[idx] = { ...next[idx], importe: +Number(value || 0).toFixed(2), importe_manual: true };
    return { ...f, line_items: next };
  });
  const addItem = () => setForm((f) => ({
    ...f, line_items: [...(f.line_items || []), { descripcion: "", cantidad: 1, precio_unitario: 0, importe: 0 }],
  }));
  const removeItem = (idx) => setForm((f) => ({
    ...f, line_items: (f.line_items || []).filter((_, i) => i !== idx),
  }));

  // Cuotas simples
  const addCuota = () => setForm((f) => ({
    ...f,
    cuotas: [...(f.cuotas || []), {
      numero: (f.cuotas?.length || 0) + 1,
      fecha: todayISO(),
      importe: 0,
      cobrada: false,
    }],
  }));
  const updateCuota = (idx, patch) => setForm((f) => {
    const next = [...(f.cuotas || [])];
    next[idx] = { ...next[idx], ...patch };
    return { ...f, cuotas: next };
  });
  const removeCuota = (idx) => setForm((f) => ({
    ...f, cuotas: (f.cuotas || []).filter((_, i) => i !== idx).map((c, i) => ({ ...c, numero: i + 1 })),
  }));

  // Cuotas recurrentes
  const setRec = (patch) => setForm((f) => ({
    ...f, cuotas_recurrentes: { num_cuotas: 12, importe_cuota: 0, dia_cobro: 1, fecha_inicio: todayISO(), ...(f.cuotas_recurrentes || {}), ...patch },
  }));

  // Validaciones cliente
  const errors = useMemo(() => {
    const errs = [];
    if (econ.precio_final < 0) errs.push("El precio final no puede ser negativo");
    if (Number(form.entrada || 0) > econ.precio_final + 0.01) {
      errs.push(`La entrada (${fmtEur(form.entrada)}) supera el precio final (${fmtEur(econ.precio_final)})`);
    }
    if (cuotasMode === "simple" && (form.cuotas || []).length > 0) {
      const suma = (form.cuotas || []).reduce((a, c) => a + Number(c.importe || 0), 0);
      if (suma > econ.saldo + 0.01) {
        errs.push(`Las cuotas suman ${fmtEur(suma)} y superan el saldo pendiente ${fmtEur(econ.saldo)}`);
      }
    }
    return errs;
  }, [econ, form.entrada, form.cuotas, cuotasMode]);

  const submit = async (e) => {
    e.preventDefault();
    if (errors.length > 0) { toast.error(errors[0]); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        precio_lista: econ.precio_lista,
        descuento_pct: econ.descuento_pct,
        descuento_importe: econ.descuento_importe,
        precio_venta: econ.precio_venta,
        precio_final: econ.precio_final,
        total: econ.precio_final,
        saldo: econ.saldo,
        cuotas: cuotasMode === "simple" ? (form.cuotas || []) : [],
        cuotas_recurrentes: cuotasMode === "recurrente" ? form.cuotas_recurrentes : null,
      };
      if (contract?.id) {
        await api.patch(`/contracts/${contract.id}`, payload);
        toast.success("Contrato actualizado");
      } else {
        if (!studentId) { toast.error("Abre la ficha del alumno para crear un contrato."); setSaving(false); return; }
        await api.post("/contracts", { ...payload, student_id: studentId });
        toast.success("Contrato creado");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
        <form onSubmit={submit} className="space-y-4">
          {/* 1 · Datos generales */}
          <div className="card-surface p-3">
            <div className="text-sm font-semibold mb-2">1 · Datos generales</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-semibold mb-1 block">Fecha *</label>
                <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required data-testid="contract-fecha" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Permiso</label>
                <select value={form.permiso} onChange={(e) => setForm({ ...form, permiso: e.target.value })} data-testid="contract-permiso" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                  {["B", "B+E", "A", "A1", "A2", "AM", "C", "C+E", "D", "D+E"].map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold mb-1 block">Pack (autorrellena líneas)</label>
                <select value={form.pack_id || ""} onChange={(e) => applyPack(e.target.value)} data-testid="contract-pack" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option value="">— Sin pack (personalizado) —</option>
                  {packs.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.clases_practicas ? ` · ${p.clases_practicas} clases` : ""}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 2 · Líneas económicas */}
          <div className="card-surface p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4" /> 2 · Productos y servicios contratados</div>
              <button type="button" onClick={addItem} className="btn-secondary h-8 px-2 text-xs" data-testid="contract-add-line"><Plus className="h-3.5 w-3.5" /> Añadir línea</button>
            </div>
            {(form.line_items || []).length === 0 && (
              <p className="text-xs italic text-neutral-500">Sin líneas — selecciona un pack o añade líneas manualmente.</p>
            )}
            {(form.line_items || []).length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase text-neutral-600 border-b border-neutral-200">
                      <th className="px-2 py-1 font-semibold" style={{ width: "50%" }}>Descripción</th>
                      <th className="px-2 py-1 font-semibold text-right" style={{ width: "12%" }}>Cantidad</th>
                      <th className="px-2 py-1 font-semibold text-right" style={{ width: "16%" }}>Precio unit.</th>
                      <th className="px-2 py-1 font-semibold text-right" style={{ width: "16%" }} title="Editable: escribe el precio pactado y el descuento se calcula solo">Importe / P. venta</th>
                      <th className="px-1 py-1" style={{ width: "6%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.line_items || []).map((it, idx) => (
                      <tr key={idx} className="border-b border-neutral-100" data-testid={`contract-line-${idx}`}>
                        <td className="px-1 py-1">
                          {/* V1.42.10 · Descripción con datalist de packs.
                              Al elegir un pack coincidente vuelca el precio
                              unitario "todo incluido" (matrícula + tasa +
                              clases + re-presentación). El precio sigue
                              siendo editable manualmente. */}
                          <input
                            type="text"
                            list={`pack-options-${idx}`}
                            value={it.descripcion}
                            onChange={(e) => {
                              const v = e.target.value;
                              const match = (packs || []).find((p) => (p.name || "").toLowerCase() === v.toLowerCase());
                              if (match) {
                                updateItem(idx, {
                                  descripcion: match.name,
                                  precio_unitario: packUnitPrice(match),
                                });
                              } else {
                                updateItem(idx, { descripcion: v });
                              }
                            }}
                            placeholder="Escribe o elige un pack…"
                            data-testid={`contract-line-desc-${idx}`}
                            className="w-full rounded border border-neutral-300 bg-white px-2 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                          />
                          <datalist id={`pack-options-${idx}`}>
                            {(packs || []).map((p) => (
                              <option key={p.id} value={p.name}>
                                {`${packUnitPrice(p).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
                              </option>
                            ))}
                          </datalist>
                        </td>
                        <td className="px-1 py-1"><input type="number" step="0.5" value={it.cantidad} onChange={(e) => updateItem(idx, { cantidad: parseFloat(e.target.value) || 0 })} data-testid={`contract-line-qty-${idx}`} className="w-full rounded border border-neutral-300 bg-white px-2 h-8 text-sm text-right focus:outline-none focus:ring-1 focus:ring-orange-500" /></td>
                        <td className="px-1 py-1"><input type="number" step="0.01" value={it.precio_unitario} onChange={(e) => updateItem(idx, { precio_unitario: parseFloat(e.target.value) || 0 })} data-testid={`contract-line-unit-${idx}`} className="w-full rounded border border-neutral-300 bg-white px-2 h-8 text-sm text-right focus:outline-none focus:ring-1 focus:ring-orange-500" /></td>
                        <td className="px-1 py-1"><input type="number" step="0.01" value={it.importe} onChange={(e) => setItemImporte(idx, parseFloat(e.target.value) || 0)} title="Precio de venta de la línea — al cambiarlo el descuento y el IVA se calculan solos" data-testid={`contract-line-importe-${idx}`} className={"w-full rounded border px-2 h-8 text-sm text-right font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500 " + (it.importe_manual ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-neutral-300 bg-white")} /></td>
                        <td className="px-1 py-1 text-center"><button type="button" onClick={() => removeItem(idx)} className="text-red-700 hover:bg-red-50 rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-right text-sm border-t border-neutral-200 pt-2" data-testid="lines-total-iva">
                  <span className="text-xs text-neutral-500 mr-2">Total precio final con IVA:</span>
                  <span className="font-mono font-bold text-emerald-800">{fmtEur(econ.precio_final)}</span>
                </div>
              </div>
            )}
          </div>

          {/* 3 · Resumen económico en tiempo real + descuento */}
          <div className="card-surface p-3 border-2 border-orange-200 bg-orange-50/30">
            <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Calculator className="h-4 w-4 text-orange-600" /> 3 · Resumen económico</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="text-xs font-semibold mb-1 block text-emerald-800">Precio de venta (€)</label>
                <input type="number" min="0" step="0.01" value={form.precio_venta || ""} placeholder="Ej: 1099" onChange={(e) => setForm({ ...form, precio_venta: parseFloat(e.target.value) || 0 })} data-testid="contract-precio-venta" className="w-full rounded-lg border-2 border-emerald-300 bg-white px-3 h-10 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Descuento comercial (%){Number(form.precio_venta || 0) > 0 && <span className="text-emerald-700 font-normal"> · auto</span>}</label>
                <input type="number" min="0" max="100" step="0.5" value={Number(form.precio_venta || 0) > 0 ? econ.descuento_pct : form.descuento_pct} disabled={Number(form.precio_venta || 0) > 0} onChange={(e) => setForm({ ...form, descuento_pct: parseFloat(e.target.value) || 0, precio_venta: 0 })} data-testid="contract-descuento-pct" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-neutral-100 disabled:text-neutral-500" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Entrada abonada</label>
                <input type="number" min="0" step="0.01" value={form.entrada} onChange={(e) => setForm({ ...form, entrada: parseFloat(e.target.value) || 0 })} data-testid="contract-entrada" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Fecha entrada</label>
                <input type="date" value={form.fecha_entrada || ""} onChange={(e) => setForm({ ...form, fecha_entrada: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <div className="rounded-lg bg-white border border-neutral-200 p-2" data-testid="econ-precio-lista">
                <div className="text-[10px] uppercase text-neutral-500 font-semibold">Precio lista</div>
                <div className="font-mono font-semibold text-neutral-800">{fmtEur(econ.precio_lista)}</div>
              </div>
              <div className="rounded-lg bg-white border border-neutral-200 p-2" data-testid="econ-descuento">
                <div className="text-[10px] uppercase text-neutral-500 font-semibold">Descuento {econ.descuento_pct}%</div>
                <div className="font-mono font-semibold text-red-600">− {fmtEur(econ.descuento_importe)}</div>
              </div>
              <div className="rounded-lg bg-emerald-100 border-2 border-emerald-400 p-2" data-testid="econ-precio-final">
                <div className="text-[10px] uppercase text-emerald-800 font-semibold">Total final (IVA incl.)</div>
                <div className="font-mono font-bold text-emerald-900">{fmtEur(econ.precio_final)}</div>
              </div>
              <div className="rounded-lg bg-white border border-neutral-200 p-2" data-testid="econ-entrada">
                <div className="text-[10px] uppercase text-neutral-500 font-semibold">Entrada</div>
                <div className="font-mono font-semibold text-neutral-800">{fmtEur(form.entrada || 0)}</div>
              </div>
              <div className="rounded-lg bg-amber-100 border-2 border-amber-400 p-2" data-testid="econ-saldo">
                <div className="text-[10px] uppercase text-amber-800 font-semibold">Saldo pendiente</div>
                <div className="font-mono font-bold text-amber-900">{fmtEur(econ.saldo)}</div>
              </div>
            </div>

            <div className="mt-2 text-xs text-neutral-600 font-mono" data-testid="econ-iva-desglose">
              Base imponible: {fmtEur(econ.base_imponible)} · IVA (21%): {fmtEur(econ.iva_importe)} · Total con IVA: <span className="font-bold text-emerald-800">{fmtEur(econ.precio_final)}</span>
            </div>

            {errors.length > 0 && (
              <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-800" data-testid="contract-errors">
                {errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
              </div>
            )}
          </div>

          {/* 4 · Cuotas */}
          <div className="card-surface p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4" /> 4 · Fraccionamiento del saldo</div>
              <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
                <button type="button" onClick={() => setCuotasMode("simple")} data-testid="cuotas-mode-simple" className={"px-3 h-8 text-xs font-medium rounded " + (cuotasMode === "simple" ? "bg-white shadow text-orange-700" : "text-neutral-600")}>Simple</button>
                <button type="button" onClick={() => setCuotasMode("recurrente")} data-testid="cuotas-mode-recurrente" className={"px-3 h-8 text-xs font-medium rounded inline-flex items-center gap-1 " + (cuotasMode === "recurrente" ? "bg-white shadow text-orange-700" : "text-neutral-600")}><Repeat className="h-3 w-3" /> Recurrente</button>
              </div>
            </div>

            {cuotasMode === "simple" && (
              <div className="space-y-2">
                {(form.cuotas || []).map((c, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`cuota-${idx}`}>
                    <div className="col-span-1 text-xs font-mono text-neutral-500">#{c.numero}</div>
                    <div className="col-span-5"><input type="date" value={c.fecha} onChange={(e) => updateCuota(idx, { fecha: e.target.value })} data-testid={`cuota-fecha-${idx}`} className="w-full rounded border border-neutral-300 bg-white px-2 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500" /></div>
                    <div className="col-span-4"><input type="number" min="0" step="0.01" value={c.importe} onChange={(e) => updateCuota(idx, { importe: parseFloat(e.target.value) || 0 })} data-testid={`cuota-importe-${idx}`} placeholder="Importe €" className="w-full rounded border border-neutral-300 bg-white px-2 h-8 text-sm text-right focus:outline-none focus:ring-1 focus:ring-orange-500" /></div>
                    <div className="col-span-1"><label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={!!c.cobrada} onChange={(e) => updateCuota(idx, { cobrada: e.target.checked })} className="h-3.5 w-3.5 accent-emerald-500" />OK</label></div>
                    <div className="col-span-1 text-right"><button type="button" onClick={() => removeCuota(idx)} className="text-red-700 hover:bg-red-50 rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button></div>
                  </div>
                ))}
                <button type="button" onClick={addCuota} className="btn-secondary h-8 px-2 text-xs" data-testid="cuota-add"><Plus className="h-3.5 w-3.5" /> Añadir cuota</button>
                {(form.cuotas || []).length > 0 && (
                  <div className="text-xs text-neutral-600 mt-1 font-mono">
                    Σ cuotas: {fmtEur((form.cuotas || []).reduce((a, c) => a + Number(c.importe || 0), 0))} / Saldo: {fmtEur(econ.saldo)}
                  </div>
                )}
              </div>
            )}

            {cuotasMode === "recurrente" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-semibold mb-1 block">Nº cuotas</label>
                  <input type="number" min="1" max="120" value={form.cuotas_recurrentes?.num_cuotas || 12} onChange={(e) => setRec({ num_cuotas: parseInt(e.target.value, 10) || 1 })} data-testid="cuotas-rec-num" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1 block">Importe/cuota</label>
                  <input type="number" min="0" step="0.01" value={form.cuotas_recurrentes?.importe_cuota || 0} onChange={(e) => setRec({ importe_cuota: parseFloat(e.target.value) || 0 })} data-testid="cuotas-rec-importe" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1 block">Día cobro (1-28)</label>
                  <input type="number" min="1" max="28" value={form.cuotas_recurrentes?.dia_cobro || 1} onChange={(e) => setRec({ dia_cobro: parseInt(e.target.value, 10) || 1 })} data-testid="cuotas-rec-dia" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1 block">Fecha inicio</label>
                  <input type="date" value={form.cuotas_recurrentes?.fecha_inicio || todayISO()} onChange={(e) => setRec({ fecha_inicio: e.target.value })} data-testid="cuotas-rec-fecha" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                {cuotasPreview && (
                  <div className="md:col-span-4 text-xs text-neutral-700 bg-neutral-50 rounded-lg p-2 font-mono" data-testid="cuotas-rec-preview">
                    {form.cuotas_recurrentes?.num_cuotas} cuotas × {fmtEur(form.cuotas_recurrentes?.importe_cuota)} · Total planificado: {fmtEur(cuotasPreview.suma)} · Última cuota: {cuotasPreview.ultima}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 5 · Consentimientos */}
          <div className="card-surface p-3 space-y-2 text-sm">
            <div className="font-semibold mb-1">5 · Consentimientos</div>
            {[
              ["proteccion_datos", "Acepta política de protección de datos"],
              ["uso_imagen", "Autoriza uso de imagen y redes sociales"],
              ["autorizacion_dgt", "Autoriza gestión documental ante DGT"],
              ["promociones", "Acepta comunicaciones comerciales"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" checked={!!form.consents[k]} onChange={(e) => setForm({ ...form, consents: { ...form.consents, [k]: e.target.checked } })} data-testid={`contract-consent-${k}`} className="h-4 w-4 accent-orange-500" />
                {label}
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            {onCancel && <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>}
            <button type="submit" disabled={saving || errors.length > 0} className="btn-primary" data-testid="contract-save">
              {saving ? "Guardando…" : "Guardar contrato"}
            </button>
          </div>
        </form>
  );
}

export default function ContractDialog({ open, studentId, contract, onClose, onSaved }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle>{contract?.id ? `Editar contrato ${contract.numero}` : "Nuevo contrato"}</DialogTitle>
        </DialogHeader>
        <ContractForm active={open} studentId={studentId} contract={contract} onSaved={onSaved} onCancel={onClose} />
      </DialogContent>
    </Dialog>
  );
}
